import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LifeBuoy, RefreshCw, MessageSquare, Send, UserPlus, Trash2, ChevronLeft, Filter, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

const STATUSES = [
  { key: "all",          label: "All" },
  { key: "open",         label: "Open" },
  { key: "in_progress",  label: "In progress" },
  { key: "waiting_user", label: "Awaiting user" },
  { key: "resolved",     label: "Resolved" },
  { key: "closed",       label: "Closed" },
];

const STATUS_PILL = {
  open:         "bg-amber-500/15 text-amber-300 border-amber-500/40",
  in_progress:  "bg-sky-500/15 text-sky-300 border-sky-500/40",
  waiting_user: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  resolved:     "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  closed:       "bg-zinc-500/15 text-zinc-400 border-zinc-500/40",
};

function TicketDetail({ ticketId, onBack, onChange }) {
  const [t, setT] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/support/tickets/${ticketId}`);
      setT(r.data?.ticket || null);
      setMsgs(r.data?.messages || []);
    } catch (err) { toast.error(errorMessage(err, "Could not load")); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${ticketId}/messages`, { body: reply });
      setReply("");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(errorMessage(err, "Reply failed")); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/admin/support/tickets/${ticketId}/status`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      await load();
      onChange && onChange();
    } catch (err) { toast.error(errorMessage(err, "Status update failed")); }
  };

  if (!t) return <div className="text-white/50 py-12 text-center">Loading…</div>;
  return (
    <div data-testid={`admin-ticket-detail-${ticketId}`} className="space-y-3">
      <button onClick={onBack} className="text-xs text-gold inline-flex items-center gap-1 hover:underline">
        <ChevronLeft className="w-3 h-3" /> Back to queue
      </button>
      <div className="rounded-md border border-gold/20 bg-black/30 p-4">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-white font-display text-lg">{t.subject}</div>
            <div className="text-xs text-white/55 mt-1">
              {t.user_name || t.user_email} · {t.user_email} · {t.category} · {t.priority} priority
            </div>
            <div className="text-[11px] text-white/40 mt-0.5">
              Created {new Date(t.created_at).toLocaleString()}
              {t.page_url && <> · <a href={t.page_url} target="_blank" rel="noreferrer" className="underline hover:text-gold">{t.page_url.slice(0, 80)}</a></>}
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_PILL[t.status] || STATUS_PILL.open}`}>
            {t.status.replace("_", " ")}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-3 text-xs">
          <button onClick={() => setStatus("in_progress")} className="text-sky-300 border border-sky-500/40 hover:bg-sky-500/10 rounded px-2.5 py-1">In progress</button>
          <button onClick={() => setStatus("resolved")} className="text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10 rounded px-2.5 py-1">Resolved</button>
          <button onClick={() => setStatus("closed")} className="text-zinc-300 border border-zinc-500/40 hover:bg-zinc-500/10 rounded px-2.5 py-1">Close</button>
        </div>
      </div>

      <div className="space-y-2">
        {msgs.map((m) => (
          <div key={m.id}
            className={`rounded-md p-3 border ${
              m.author_role === "support"
                ? "bg-gold/5 border-gold/25 ml-6"
                : "bg-white/5 border-white/10 mr-6"
            }`}
          >
            <div className="text-[11px] text-white/55 mb-1">
              <span className="text-gold/85">{m.author_name}</span>
              <span> · {m.author_role === "support" ? "support" : "user"}</span>
              <span> · {new Date(m.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm text-white/90 whitespace-pre-wrap">{m.body}</div>
          </div>
        ))}
      </div>

      <div className="flex items-end gap-2 pt-2">
        <textarea
          data-testid="admin-reply-body"
          value={reply} onChange={(e) => setReply(e.target.value)}
          placeholder="Reply to user…" rows={3}
          className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
        />
        <button
          data-testid="admin-reply-send"
          onClick={sendReply} disabled={busy || !reply.trim()}
          className="inline-flex items-center gap-1 bg-gold text-black hover:bg-gold/90 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          <Send className="w-4 h-4" /> Send
        </button>
      </div>
    </div>
  );
}

function AgentsPanel({ adminOnly }) {
  const [agents, setAgents] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/support/agents");
      setAgents(r.data || []);
    } catch (err) { toast.error(errorMessage(err, "Load failed")); }
  };
  useEffect(() => { if (adminOnly) load(); /* eslint-disable-next-line */ }, [adminOnly]);

  if (!adminOnly) return null;

  const promote = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/support/agents", { email: email.trim().toLowerCase() });
      toast.success("Promoted to support agent");
      setEmail(""); await load();
    } catch (err) { toast.error(errorMessage(err, "Promotion failed")); }
    finally { setBusy(false); }
  };
  const revoke = async (em) => {
    if (!window.confirm(`Revoke support_agent role from ${em}?`)) return;
    try {
      await api.delete(`/admin/support/agents/${em}`);
      toast.success("Revoked"); await load();
    } catch (err) { toast.error(errorMessage(err, "Revoke failed")); }
  };

  return (
    <div className="rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-fuchsia-300" />
          <div className="text-fuchsia-200 font-display text-base">Support Agents</div>
        </div>
      </div>
      <div className="text-xs text-white/65 mb-3">
        Promote a registered user to <code>support_agent</code> — they get the support tab and nothing else.
        Useful for outside agencies you hire for 24-hour coverage.
      </div>
      <div className="flex items-center gap-2 mb-3">
        <input
          data-testid="agent-email-input"
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="user@agency.com (must already have an account)"
          className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
        />
        <button
          data-testid="agent-promote-btn"
          onClick={promote} disabled={busy || !email}
          className="inline-flex items-center gap-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-luxury-black rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50"
        >
          <UserPlus className="w-4 h-4" /> Promote
        </button>
      </div>
      <div className="space-y-1">
        {agents.length === 0 && <div className="text-xs text-white/40">No support agents yet.</div>}
        {agents.map((a) => (
          <div key={a.email} className="flex items-center justify-between text-xs border border-white/10 rounded px-3 py-2">
            <div>
              <span className="text-white">{a.name || a.email}</span>
              <span className="text-white/50"> · {a.email} · {a.role}</span>
            </div>
            {a.role === "support_agent" && (
              <button onClick={() => revoke(a.email)} className="text-rose-300 hover:bg-rose-500/10 border border-rose-500/40 rounded px-2 py-1 inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Revoke
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminSupportTab({ adminOnly = false }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ open: 0, in_progress: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const r = await api.get("/admin/support/tickets", { params: { ...params, limit: 100 } });
      setItems(r.data?.items || []);
      setCounts({ open: r.data?.open || 0, in_progress: r.data?.in_progress || 0 });
    } catch (err) {
      toast.error(errorMessage(err, "Load failed"));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (!activeId) load(); /* eslint-disable-next-line */ }, [statusFilter, activeId]);

  if (activeId) {
    return <TicketDetail ticketId={activeId} onBack={() => setActiveId(null)} onChange={load} />;
  }

  return (
    <div data-testid="admin-support-tab" className="space-y-4">
      <div className="rounded-md border border-gold/30 bg-gold/5 p-4 flex items-start gap-3">
        <LifeBuoy className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-gold font-display text-base">Support Queue</div>
          <div className="text-xs text-white/65 mt-1">
            Tickets opened by users via /support. {counts.open} open · {counts.in_progress} in progress.
          </div>
        </div>
        <button onClick={load} className="text-white/50 hover:text-gold p-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <AgentsPanel adminOnly={adminOnly} />

      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-gold/70" />
        {STATUSES.map((s) => (
          <button
            key={s.key} data-testid={`support-filter-${s.key}`}
            onClick={() => setStatusFilter(s.key)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              statusFilter === s.key
                ? "bg-gold/15 text-gold border-gold"
                : "border-white/10 text-white/60 hover:border-gold/40 hover:text-gold"
            }`}
          >{s.label}</button>
        ))}
      </div>

      {loading && <div className="text-center text-white/50 py-10">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center text-white/50 py-12 border border-dashed border-white/10 rounded-md">
          No tickets. Quiet day.
        </div>
      )}
      <div className="space-y-2">
        {items.map((t) => (
          <motion.button
            key={t.id} data-testid={`support-ticket-${t.id}`}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => setActiveId(t.id)}
            className="w-full text-left rounded-md border border-white/10 hover:border-gold/30 bg-black/30 p-3 transition-colors"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <MessageSquare className="w-4 h-4 text-gold/70 flex-shrink-0" />
                <span className="text-sm text-white truncate">{t.subject}</span>
              </div>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${STATUS_PILL[t.status] || STATUS_PILL.open}`}>
                {t.status.replace("_", " ")}
              </span>
            </div>
            <div className="text-xs text-white/50 mt-1">
              {t.user_name || t.user_email} · {t.category} · {t.priority}
              {t.assigned_to && <> · assigned: <span className="text-gold/70">{t.assigned_to}</span></>}
              <span className="text-white/40"> · updated {new Date(t.updated_at).toLocaleString()}</span>
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
